import { ChangeDetectionStrategy, Component, computed, inject, input, resource } from '@angular/core';
import { RegisterTabComponent } from '@core/decorators/register-tab-component.decorator';
import { DeliveryTrackerService } from '@features/delivery-tracker/services/delivery-tracker.service';
import { EmailListTableConfigService } from '@features/email-communication/services/email-list-table-config.service';
import { DeliveryTrackerEmailListComponent } from '@features/organization/components/delivery-tracker-detail/components/delivery-tracker-email-list/delivery-tracker-email-list.component';
import { OrganizationTabKeys } from '@features/organization/constants/organization-tab-keys.constant';
import { ReminderTrackerPresenceBadgeComponent } from '@features/reminder-tracker/components/reminder-tracker-presence-badge/reminder-tracker-presence-badge.component';
import { ReminderTrackerPresenceService } from '@features/reminder-tracker/services/reminder-tracker-presence.service';
import type { ReminderTrackerPresenceEntry } from '@features/reminder-tracker/types/reminder-tracker-presence.types';
import { TranslocoDirective } from '@jsverse/transloco';
import type { EntityState } from '@kerberos-compliance/compliance-shared-ts/common/enums/entity-state.enum';
import { EntityStateMap } from '@kerberos-compliance/compliance-shared-ts/common/enums/entity-state.enum';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import type { ResponseDeliveryTrackerCacheDto } from '@kerberos-compliance/compliance-shared-ts/features/delivery/dtos/response.delivery-tracker.cache-dto';
import {
  DateFormatConfigurations,
  LoaderComponent,
  localizeDate,
  TranslationService,
} from '@kerberos-compliance/compliance-shared-ui';
import dayjs from 'dayjs';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { DeliveryTrackerDetailFacade } from './facades/delivery-tracker-detail.facade';
import { DeliveryTrackerDocumentFacade } from './facades/delivery-tracker-document.facade';
import { DeliveryTrackerDocumentService } from './services/delivery-tracker-document.service';
import { DeliveryTrackerDetailStore } from './stores/delivery-tracker-detail.store';
import { DeliveryTrackerDetailUiStore } from './stores/delivery-tracker-detail-ui.store';
import { DeliveryTrackerDocumentStore } from './stores/delivery-tracker-document.store';

@RegisterTabComponent(OrganizationTabKeys.DeliveryTracker)
@Component({
  selector: 'aad-delivery-tracker-detail',
  imports: [
    NzCollapseModule,
    TranslocoDirective,
    LoaderComponent,
    DeliveryTrackerEmailListComponent,
    ReminderTrackerPresenceBadgeComponent,
  ],
  providers: [
    DeliveryTrackerDetailUiStore,
    DeliveryTrackerDetailFacade,
    DeliveryTrackerService,
    EmailListTableConfigService,
    DeliveryTrackerDetailStore,
    ReminderTrackerPresenceService,
    DeliveryTrackerDocumentStore,
    DeliveryTrackerDocumentService,
    DeliveryTrackerDocumentFacade,
  ],
  templateUrl: 'delivery-tracker-detail.component.html',
  styleUrl: 'delivery-tracker-detail.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryTrackerDetailComponent {
  private readonly translationService = inject(TranslationService);

  protected readonly deliveryTrackerDetailFacade = inject(DeliveryTrackerDetailFacade);

  public readonly organizationId = input<UUID>();

  public readonly organizationState = input<EntityState>();

  protected readonly periods = this.deliveryTrackerDetailFacade.periods;

  protected readonly loading = this.deliveryTrackerDetailFacade.loading;

  protected readonly selectedPeriodId = this.deliveryTrackerDetailFacade.selectedPeriodId;

  protected readonly reminderTrackerPresence = this.deliveryTrackerDetailFacade.reminderTrackerPresence;

  protected readonly reminderTrackerPresenceLoaded = this.deliveryTrackerDetailFacade.reminderTrackerPresenceLoaded;

  protected readonly hasActiveNotifiableUser = this.deliveryTrackerDetailFacade.hasActiveNotifiableUser;

  /**
   * Whether the organization's contract is inactive (state is anything other than Active). Drives the
   * "Contract inactive" stop reason on the reminder badge — reminders are not sent while the contract is inactive.
   */
  protected readonly isContractInactive = computed(() => {
    const state = this.organizationState();
    return state === undefined ? false : state !== EntityStateMap.Active;
  });

  /**
   * Returns the reminder tracker presence entry for the given delivery tracker, or undefined if the
   * presence has not finished loading yet (in which case nothing should render).
   *
   * @param {UUID} trackerId - The delivery tracker ID.
   * @returns {ReminderTrackerPresenceEntry | undefined} The presence entry (possibly with `trackerStatus === undefined` when no row exists), or undefined while still loading.
   */
  protected getReminderTrackerPresence(trackerId: UUID): ReminderTrackerPresenceEntry | undefined {
    if (!this.reminderTrackerPresenceLoaded()) {
      return undefined;
    }
    return this.reminderTrackerPresence().get(trackerId) ?? {};
  }

  /**
   * Determines whether the current date falls within the specified period.
   *
   * @param {object} param0 - The input object containing period boundaries.
   * @param {string|Date} param0.periodStart - The start of the period.
   * @param {string|Date} param0.periodEnd - The end of the period.
   * @returns {boolean} `true` if the current date is within the period; otherwise, `false`.
   */
  protected readonly isCurrentPeriod = ({ periodStart, periodEnd }: ResponseDeliveryTrackerCacheDto): boolean => {
    if (!periodStart || !periodEnd) {
      return false;
    }
    const today = dayjs();
    return !today.isBefore(periodStart) && !today.isAfter(periodEnd);
  };

  // noinspection JSUnusedGlobalSymbols - placeholder to keep the resource
  protected retrieveDataOnIdChange = resource({
    params: () => this.organizationId(),
    loader: () => this.deliveryTrackerDetailFacade.loadDeliveryTrackerByOrganizationId(this.organizationId()),
  });

  /**
   * Handles accordion expansion/collapse for a specific period.
   * Loads emails for the period when expanded if not already loaded.
   *
   * @param {UUID} trackerId - The tracker ID of the period being toggled
   * @param {boolean} isExpanded - Whether the panel is now expanded
   * @returns {Promise<void>} A promise that resolves once the expansion state has been applied
   */
  protected async onPeriodExpandChange(trackerId: UUID, isExpanded: boolean): Promise<void> {
    await this.deliveryTrackerDetailFacade.onPeriodExpandChange(trackerId, isExpanded);
  }

  /**
   * Checks if a specific period is currently selected/expanded.
   *
   * @param {UUID} trackerId - The tracker ID to check
   * @returns {boolean} true if the period is selected
   */
  protected isPeriodSelected(trackerId: UUID): boolean {
    return this.selectedPeriodId() === trackerId;
  }

  /**
   * Formats the period range (e.g. "01 Jan 22 – 31 Mar 22").
   *
   * @param {ResponseDeliveryTrackerCacheDto} tracker - The tracker
   * @returns {string} The formatted period range
   */
  protected getPeriodLabel(tracker: ResponseDeliveryTrackerCacheDto): string {
    if (!tracker.periodStart || !tracker.periodEnd) {
      return '';
    }
    const language = this.translationService.activeLanguage();
    return `${localizeDate(tracker.periodStart, language, DateFormatConfigurations.dateFormat, false)} – ${localizeDate(tracker.periodEnd, language, DateFormatConfigurations.dateFormat, false)}`;
  }
}
